import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Drawer,
  Descriptions,
  Typography,
  DatePicker,
  Divider,
  Alert
} from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import {
  PlusOutlined,
  ImportOutlined,
  ExportOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { PackingList, PackingDetail, PackingListFilter, PackingItem } from '../../types'
import { packingService, ImportResult, PackingStatistics, PlanItemDraft } from '../../services/packing'
import { useAuth } from '../../hooks/useAuth'
import { PageHeader } from '../../components/common'
import { PackingImportModal, PackingExportModal, PackingItemsDisplay, PackingItemsComparison, AnomalyBadges } from '../../components/Packing'

const { Option } = Select
const { TextArea } = Input
const { Title } = Typography
const { RangePicker } = DatePicker

interface PackingManagementProps {}

const PackingManagement: React.FC<PackingManagementProps> = () => {
  const { user } = useAuth()
  const [packingLists, setPackingLists] = useState<PackingList[]>([])
  const [loading, setLoading] = useState(false)
  const [statistics, setStatistics] = useState<PackingStatistics | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  
  // 模态框状态
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [importModalVisible, setImportModalVisible] = useState(false)
  const [exportModalVisible, setExportModalVisible] = useState(false)
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  // 装箱明细编辑弹窗（新增 / 编辑共用，弹窗内同时维护箱子信息与计划明细）
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [detailModalMode, setDetailModalMode] = useState<'create' | 'edit'>('create')
  const [planDraft, setPlanDraft] = useState<PlanItemDraft[]>([])
  const [planResolving, setPlanResolving] = useState(false)
  const [detailSubmitting, setDetailSubmitting] = useState(false)
  const planPreviewTimer = useRef<number | null>(null)
  
  // 表单和数据状态
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [detailForm] = Form.useForm()
  const [currentPackingList, setCurrentPackingList] = useState<PackingList | null>(null)
  const [currentPackingDetail, setCurrentPackingDetail] = useState<PackingDetail | null>(null)
  const [packingDetails, setPackingDetails] = useState<PackingDetail[]>([])
  const [filter, setFilter] = useState<PackingListFilter>({})
  const [searchForm] = Form.useForm()
  
  // 展开状态管理
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])
  const [allExpanded, setAllExpanded] = useState(false)

  // 加载数据
  const loadPackingLists = async (filterToUse?: PackingListFilter) => {
    setLoading(true)
    try {
      const data = await packingService.getPackingLists(filterToUse || filter)
      setPackingLists(data)
    } catch (error) {
      message.error('加载装箱清单失败')
    } finally {
      setLoading(false)
    }
  }

  const loadStatistics = async () => {
    try {
      const data = await packingService.getPackingStatistics()
      setStatistics(data)
    } catch (error) {
      console.error('加载统计数据失败:', error)
    }
  }

  const loadPackingDetails = async (listId: string): Promise<PackingDetail[]> => {
    try {
      const details = await packingService.getPackingDetails(listId)
      setPackingDetails(details)
      return details
    } catch (error) {
      message.error('加载装箱明细失败')
      return []
    }
  }

  // 初始加载
  useEffect(() => {
    loadPackingLists()
    loadStatistics()
  }, [])

  // 状态标签渲染
  const renderStatusTag = (status: string) => {
    const statusMap = {
      // 小写状态值（数据库存储格式）
      unpacked: { color: 'default', text: '未装箱' },
      packing: { color: 'processing', text: '装箱中' },
      completed: { color: 'success', text: '已完成' },
      // 大写状态值（Prisma 枚举格式）
      UNPACKED: { color: 'default', text: '未装箱' },
      PACKING: { color: 'processing', text: '装箱中' },
      COMPLETED: { color: 'success', text: '已完成' }
    }
    const config = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  // 表格列定义
  const columns: ColumnsType<PackingList> = [
    {
      title: '装箱号',
      dataIndex: 'packingNumber',
      key: 'packingNumber',
      width: 180,
      fixed: 'left'
    },
    {
      title: '款号',
      dataIndex: 'styleNumber',
      key: 'styleNumber',
      width: 120
    },
    {
      title: 'PO订单号',
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 120
    },
    {
      title: '箱数',
      key: 'boxCount',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <span>
          {record.packedBoxCount ?? 0}/{record.boxCount}
        </span>
      )
    },
    {
      title: '件数',
      key: 'quantity',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <span style={{ 
          color: record.actualTotalQty !== record.plannedTotalQty ? '#ff4d4f' : 'inherit' 
        }}>
          {record.actualTotalQty}/{record.plannedTotalQty}
        </span>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatusTag
    },
    {
      title: '异常',
      key: 'anomalies',
      width: 220,
      render: (_, record) => <AnomalyBadges anomalies={record.anomalies} />
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: '最后操作时间',
      dataIndex: 'lastOperationTime',
      key: 'lastOperationTime',
      width: 160,
      render: (text: string | null) =>
        text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            查看
          </Button>
          {(user?.role === 'ADMIN' || user?.role === 'BUSINESS') && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                编辑
              </Button>
              <Popconfirm
                title="确定要删除这个装箱清单吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                >
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ]

  // 装箱明细表格列定义
  const detailColumns: ColumnsType<PackingDetail> = [
    {
      title: '箱号',
      dataIndex: 'boxSequence',
      key: 'boxSequence',
      width: 70,
      align: 'center'
    },
    {
      title: '颜色码',
      dataIndex: 'colorCode',
      key: 'colorCode',
      width: 100
    },
    {
      title: '颜色描述',
      dataIndex: 'colorDescription',
      key: 'colorDescription',
      width: 120
    },
    {
      title: '总数量',
      dataIndex: 'totalQty',
      key: 'totalQty',
      width: 90,
      align: 'center'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatusTag
    },
    {
      title: '异常',
      key: 'anomalies',
      width: 200,
      render: (_, record) => <AnomalyBadges anomalies={record.anomalies} showCount={true} />
    },
    {
      title: '中文颜色',
      dataIndex: 'colorChinese',
      key: 'colorChinese',
      width: 100
    },
    {
      title: '净重(kg)',
      dataIndex: 'netWeight',
      key: 'netWeight',
      width: 90,
      align: 'center',
      render: (value: any) => {
        const numValue = Number(value)
        return !isNaN(numValue) ? numValue.toFixed(2) : '-'
      }
    },
    {
      title: '毛重(kg)',
      dataIndex: 'grossWeight',
      key: 'grossWeight',
      width: 90,
      align: 'center',
      render: (value: any) => {
        const numValue = Number(value)
        return !isNaN(numValue) ? numValue.toFixed(2) : '-'
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => {
        // 只有未完成的装箱明细才能编辑
        const canEdit = currentPackingList && 
          (currentPackingList.status === 'UNPACKED' || currentPackingList.status === 'PACKING')
        
        if (!canEdit || (user?.role !== 'ADMIN' && user?.role !== 'BUSINESS')) {
          return null
        }

        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditDetail(record)}
            >
              编辑
            </Button>
            <Popconfirm
              title="确定要删除这个装箱明细吗？"
              onConfirm={() => handleDeleteDetail(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    }
  ]

  // 渲染装箱明细的展开内容
  // 注：计划明细的增删改已合并到「编辑装箱明细」弹窗，这里只做展示
  const renderExpandedRow = (record: PackingDetail) => {
    const plannedItems = Array.isArray(record.plannedItems) 
      ? record.plannedItems 
      : []
    
    const actualItems = Array.isArray(record.actualItems) 
      ? record.actualItems 
      : []

    // 如果有实际装箱数据，显示对比视图
    if (actualItems.length > 0) {
      return (
        <div style={{ padding: '4px 16px 8px', backgroundColor: '#fafafa' }}>
          <PackingItemsComparison
            plannedItems={plannedItems}
            actualItems={actualItems}
            colorDescription={record.colorDescription || record.colorChinese}
          />
        </div>
      )
    }

    // 否则只显示计划装箱
    return (
      <div style={{ padding: '4px 16px 8px', backgroundColor: '#fafafa' }}>
        <PackingItemsDisplay 
          items={plannedItems}
          title="计划装箱明细"
          showTotal={true}
          colorDescription={record.colorDescription || record.colorChinese}
        />
      </div>
    )
  }

  // 搜索处理函数
  const handleSearch = async () => {
    const values = searchForm.getFieldsValue()
    const newFilter: PackingListFilter = {
      styleNumber: values.styleNumber?.trim() || undefined,
      poNumber: values.poNumber?.trim() || undefined,
      status: values.status || undefined,
      dateFrom: values.dateRange?.[0]?.startOf('day').toISOString() || undefined,
      dateTo: values.dateRange?.[1]?.endOf('day').toISOString() || undefined
    }
    setFilter(newFilter)
    // 直接传递新的filter，避免状态更新延迟
    await loadPackingLists(newFilter)
  }

  const handleResetSearch = () => {
    searchForm.resetFields()
    const emptyFilter = {}
    setFilter(emptyFilter)
    // 直接传递空filter
    loadPackingLists(emptyFilter)
  }

  // 日期快捷选择
  const dateRangePresets: {
    label: string;
    value: [Dayjs, Dayjs];
  }[] = [
    { label: '近1天', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: '近7天', value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: '近30天', value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
  ]

  // 事件处理函数
  const handleCreate = () => {
    setCreateModalVisible(true)
  }

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields()
      await packingService.createPackingList(values)
      message.success('创建装箱清单成功')
      setCreateModalVisible(false)
      createForm.resetFields()
      loadPackingLists()
      loadStatistics()
    } catch (error) {
      message.error('创建装箱清单失败')
    }
  }

  const handleEdit = (record: PackingList) => {
    setCurrentPackingList(record)
    editForm.setFieldsValue({
      description: record.description,
      status: record.status
    })
    setEditModalVisible(true)
  }

  const handleEditSubmit = async () => {
    if (!currentPackingList) return
    
    try {
      const values = await editForm.validateFields()
      await packingService.updatePackingList(currentPackingList.id, values)
      message.success('更新装箱清单成功')
      setEditModalVisible(false)
      editForm.resetFields()
      setCurrentPackingList(null)
      loadPackingLists()
      loadStatistics()
    } catch (error) {
      message.error('更新装箱清单失败')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await packingService.deletePackingList(id)
      message.success('删除装箱清单成功')
      loadPackingLists()
      loadStatistics()
    } catch (error) {
      message.error('删除装箱清单失败')
    }
  }

  const handleViewDetails = async (record: PackingList) => {
    setCurrentPackingList(record)
    const details = await loadPackingDetails(record.id)
    setDetailDrawerVisible(true)
    
    // 默认展开有异常的箱子
    const detailsWithAnomalies = details.filter(detail => {
      const anomalies = detail.anomalies
      return anomalies && (anomalies.hasMissing || anomalies.hasExtra || anomalies.hasOffList)
    })
    
    if (detailsWithAnomalies.length > 0) {
      // 只展开有异常的箱子
      setExpandedRowKeys(detailsWithAnomalies.map(d => d.id))
      setAllExpanded(false)
    } else {
      // 如果没有异常，全部收起
      setExpandedRowKeys([])
      setAllExpanded(false)
    }
  }

  // 全部展开/收起处理
  const handleToggleExpandAll = () => {
    if (allExpanded) {
      // 收起所有
      setExpandedRowKeys([])
      setAllExpanded(false)
    } else {
      // 展开所有有明细的行
      const expandableKeys = packingDetails
        .filter(record => 
          (Array.isArray(record.plannedItems) && record.plannedItems.length > 0) ||
          (Array.isArray(record.actualItems) && record.actualItems.length > 0)
        )
        .map(record => record.id)
      setExpandedRowKeys(expandableKeys)
      setAllExpanded(true)
    }
  }

  const handleImport = () => {
    setImportModalVisible(true)
  }

  const handleImportSubmit = async (file: File) => {
    try {
      const result: ImportResult = await packingService.importPackingPlan(file)
      if (result.success) {
        // 检查是否有部分失败的情况
        if (result.message && result.message.includes('失败')) {
          // 部分成功，显示详细信息
          message.warning({
            content: result.message,
            duration: 10,
            style: { whiteSpace: 'pre-wrap' }
          })
        } else {
          message.success(`导入成功！共导入 ${result.totalBoxes} 箱，${result.totalItems} 件商品`)
        }
        
        if (result.missingSkus && result.missingSkus > 0) {
          message.warning(`有 ${result.missingSkus} 个SKU未找到，请检查导入结果`)
        }
        setImportModalVisible(false)
        loadPackingLists()
        loadStatistics()
      } else {
        message.error(result.message || '导入失败')
      }
    } catch (error) {
      message.error('导入失败')
    }
  }

  const handleExport = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要导出的装箱清单')
      return
    }
    setExportModalVisible(true)
  }

  const handleExportSubmit = async () => {
    try {
      if (selectedRowKeys.length === 0) {
        message.warning('请先选择要导出的装箱清单')
        return
      }

      const blob = await packingService.exportSelectedPackingData(selectedRowKeys as string[])
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `装箱数据导出_${new Date().toISOString().slice(0, 10)}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      message.success('导出成功')
      setExportModalVisible(false)
      setSelectedRowKeys([])
    } catch (error) {
      message.error('导出失败')
    }
  }

  const handleExportEAN = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要导出的装箱清单')
      return
    }
    handleExportEANSubmit()
  }

  const handleExportEANSubmit = async () => {
    try {
      if (selectedRowKeys.length === 0) {
        message.warning('请先选择要导出的装箱清单')
        return
      }

      const blob = await packingService.exportSelectedPackingDataEAN(selectedRowKeys as string[])
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `EAN装箱数据导出_${new Date().toISOString().slice(0, 10)}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      message.success('EAN装箱数据导出成功')
      setSelectedRowKeys([])
    } catch (error) {
      message.error('EAN装箱数据导出失败')
    }
  }

  // ===== 装箱明细编辑（箱子信息 + 计划明细，一体式编辑器）=====

  const toNullableNumber = (value: any): number | null => {
    if (value === undefined || value === null || value === '') return null
    const num = Number(value)
    return Number.isNaN(num) ? null : num
  }

  // 把明细里的计划项转成编辑草稿
  const toPlanDraft = (items?: PackingItem[]): PlanItemDraft[] =>
    (Array.isArray(items) ? items : []).map((item) => {
      const barcode = item.barcode || item.skuBarcode || null
      return {
        size: item.size || '',
        quantity: Number(item.quantity) || 0,
        barcode,
        found: !!barcode
      }
    })

  // ----- SKU 条码联动预览（防抖，避免输入颜色码时频繁请求）-----
  const schedulePlanPreview = (colorCode: string, rows: PlanItemDraft[]) => {
    if (planPreviewTimer.current) window.clearTimeout(planPreviewTimer.current)
    planPreviewTimer.current = window.setTimeout(() => {
      runPlanPreview(colorCode, rows)
    }, 400)
  }

  const runPlanPreview = async (colorCode: string, rows: PlanItemDraft[]) => {
    if (!currentPackingList) return
    const code = (colorCode || '').trim()
    if (!code || rows.length === 0) {
      setPlanDraft((prev) => prev.map((r) => ({ ...r, barcode: null, found: false })))
      return
    }
    setPlanResolving(true)
    try {
      const result = await packingService.resolvePlanPreview(
        currentPackingList.id,
        code,
        rows.map((r) => ({ size: (r.size || '').trim(), quantity: r.quantity }))
      )
      if (result && Array.isArray(result.rows)) {
        // 后端对空尺码也会返回一行，rows 与入参同序。
        // 防抖期间用户可能增删过行，这里按尺码再校验一次，避免按下标回填错位。
        setPlanDraft((prev) =>
          prev.map((r, i) => {
            const row = result.rows[i]
            if (!row) return r
            if ((r.size || '').trim() !== (row.size || '').trim()) return r
            return { ...r, barcode: row.barcode, found: row.found }
          })
        )
      }
    } catch (error) {
      // 预览失败静默处理，保存时后端仍会兜底校验
    } finally {
      setPlanResolving(false)
    }
  }

  const handleCreateDetail = () => {
    if (!currentPackingList) return
    detailForm.resetFields()
    setCurrentPackingDetail(null)
    setDetailModalMode('create')
    setPlanDraft([])
    setDetailModalVisible(true)
  }

  const handleEditDetail = (detail: PackingDetail) => {
    const draft = toPlanDraft(detail.plannedItems)
    setCurrentPackingDetail(detail)
    detailForm.setFieldsValue({
      boxSequence: detail.boxSequence,
      colorCode: detail.colorCode,
      colorDescription: detail.colorDescription,
      colorChinese: detail.colorChinese,
      netWeight: detail.netWeight,
      grossWeight: detail.grossWeight
    })
    setDetailModalMode('edit')
    setPlanDraft(draft)
    setDetailModalVisible(true)
    // 打开时以当前颜色码刷新一次条码匹配
    schedulePlanPreview(detail.colorCode, draft)
  }

  const handleDeleteDetail = async (detailId: string) => {
    try {
      await packingService.deletePackingDetail(detailId)
      message.success('删除装箱明细成功')
      if (currentPackingList) {
        await loadPackingDetails(currentPackingList.id)
        await loadPackingLists()
        await loadStatistics()
      }
    } catch (error) {
      message.error('删除装箱明细失败')
    }
  }

  // ----- 计划明细行操作 -----
  const addPlanRow = () => {
    setPlanDraft([...planDraft, { size: '', quantity: 1, barcode: null, found: false }])
  }

  const updatePlanRow = (index: number, field: 'size' | 'quantity', value: any) => {
    const next = planDraft.map((row, i) =>
      i === index
        ? { ...row, [field]: value, ...(field === 'size' ? { barcode: null, found: false } : {}) }
        : row
    )
    setPlanDraft(next)
    if (field === 'size') {
      schedulePlanPreview(detailForm.getFieldValue('colorCode'), next)
    }
  }

  const removePlanRow = (index: number) => {
    setPlanDraft(planDraft.filter((_, i) => i !== index))
  }

  const closeDetailModal = () => {
    setDetailModalVisible(false)
    setCurrentPackingDetail(null)
    setPlanDraft([])
    detailForm.resetFields()
    if (planPreviewTimer.current) window.clearTimeout(planPreviewTimer.current)
  }

  // ----- 保存（新增 / 编辑共用）-----
  const handleDetailSubmit = async () => {
    if (!currentPackingList) return

    let values: any
    try {
      values = await detailForm.validateFields()
    } catch {
      return
    }

    if (planDraft.length === 0) {
      message.error('请至少添加一个装箱尺码')
      return
    }
    if (planDraft.some((r) => !(r.size || '').trim())) {
      message.error('请填写所有行的尺码')
      return
    }
    if (planDraft.some((r) => !r.quantity || r.quantity <= 0)) {
      message.error('数量必须大于 0')
      return
    }
    const unmatched = planDraft.filter((r) => !r.found)
    if (unmatched.length > 0) {
      message.error(
        `以下尺码在颜色码 ${values.colorCode} 下没有对应 SKU：${unmatched
          .map((r) => r.size.trim())
          .join('、')}。请删除这些尺码，或修正颜色码后重试。`
      )
      return
    }

    const payload: any = {
      boxSequence: Number(values.boxSequence),
      colorCode: values.colorCode,
      colorDescription: values.colorDescription,
      colorChinese: values.colorChinese,
      netWeight: toNullableNumber(values.netWeight),
      grossWeight: toNullableNumber(values.grossWeight),
      plannedItems: planDraft.map((r) => ({ size: r.size.trim(), quantity: Number(r.quantity) }))
    }

    setDetailSubmitting(true)
    try {
      if (detailModalMode === 'create') {
        await packingService.createPackingDetail({
          packingListId: currentPackingList.id,
          ...payload
        })
        message.success('创建装箱明细成功')
      } else if (currentPackingDetail) {
        await packingService.updatePackingDetail(currentPackingDetail.id, payload)
        message.success('更新装箱明细成功')
      }
      closeDetailModal()
      await loadPackingDetails(currentPackingList.id)
      await loadPackingLists()
      await loadStatistics()
    } catch (error: any) {
      // 错误文案由 axios 拦截器统一弹出；这里在缺 SKU 时把对应行标红
      const resp = error?.response?.data
      if (resp?.code === 'MISSING_SKU' && Array.isArray(resp.missing)) {
        const sizes = new Set<string>(resp.missing.map((m: any) => String(m.size)))
        setPlanDraft((prev) =>
          prev.map((r) => (sizes.has(r.size.trim()) ? { ...r, barcode: null, found: false } : r))
        )
      }
    } finally {
      setDetailSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader title="装箱管理" />
      
      {/* 统计卡片 - 简洁显示 */}
      {statistics && (
        <Card style={{ marginBottom: 16 }} size="small">
          <Row gutter={16}>
            <Col span={6}>
              <Statistic title="总装箱清单" value={statistics.totalLists} valueStyle={{ fontSize: '20px' }} />
            </Col>
            <Col span={6}>
              <Statistic title="未装箱" value={statistics.unpackedLists} valueStyle={{ fontSize: '20px' }} />
            </Col>
            <Col span={6}>
              <Statistic title="装箱中" value={statistics.packingLists} valueStyle={{ fontSize: '20px' }} />
            </Col>
            <Col span={6}>
              <Statistic title="已完成" value={statistics.completedLists} valueStyle={{ fontSize: '20px' }} />
            </Col>
          </Row>
        </Card>
      )}

      {/* 主要内容卡片 */}
      <Card>
        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            {(user?.role === 'ADMIN' || user?.role === 'BUSINESS') && (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  新建装箱清单
                </Button>
                <Button
                  icon={<ImportOutlined />}
                  onClick={handleImport}
                >
                  导入装箱规划
                </Button>
              </>
            )}
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              导出装箱数据
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExportEAN}
            >
              导出EAN装箱数据
            </Button>
          </Space>
        </div>

        {/* 搜索筛选器 */}
        <Card style={{ marginBottom: 16 }} size="small">
          <Form form={searchForm}>
            <Row gutter={16} align="middle">
              <Col span={5}>
                <Form.Item name="styleNumber" label="款号" style={{ marginBottom: 0 }}>
                  <Input placeholder="支持模糊搜索" allowClear />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="poNumber" label="PO订单号" style={{ marginBottom: 0 }}>
                  <Input placeholder="支持模糊搜索" allowClear />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="status" label="状态" style={{ marginBottom: 0 }}>
                  <Select placeholder="请选择状态" allowClear style={{ width: '100%' }}>
                    <Option value="unpacked">未装箱</Option>
                    <Option value="packing">装箱中</Option>
                    <Option value="completed">已完成</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="dateRange" label="导入时间" style={{ marginBottom: 0 }}>
                  <RangePicker 
                    style={{ width: '100%' }}
                    presets={dateRangePresets}
                    showTime={false}
                  />
                </Form.Item>
              </Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                  >
                    搜索
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetSearch}
                  >
                    重置
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* 装箱清单表格 */}
        <Table
          columns={columns}
          dataSource={packingLists}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1600 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
            selections: [
              Table.SELECTION_ALL,
              Table.SELECTION_INVERT,
              Table.SELECTION_NONE,
            ],
          }}
          rowClassName={(record) => {
            const status = record.status.toUpperCase()
            const anomalies = record.anomalies
            
            // 异常样式优先级更高
            if (anomalies) {
              if (anomalies.hasMissing && (anomalies.hasExtra || anomalies.hasOffList)) return 'row-anomaly-mixed'
              if (anomalies.hasMissing) return 'row-anomaly-missing'
              if (anomalies.hasExtra) return 'row-anomaly-extra'
              if (anomalies.hasOffList) return 'row-anomaly-offlist'
            }
            
            // 状态样式
            if (status === 'PACKING') return 'row-packing'
            if (status === 'COMPLETED') return 'row-completed'
            return ''
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
        />
      </Card>

      {/* 创建装箱清单模态框 */}
      <Modal
        title="新建装箱清单"
        open={createModalVisible}
        onOk={handleCreateSubmit}
        onCancel={() => {
          setCreateModalVisible(false)
          createForm.resetFields()
        }}
        width={600}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="styleNumber"
            label="款号"
            rules={[{ required: true, message: '请输入款号' }]}
          >
            <Input placeholder="请输入款号" />
          </Form.Item>
          <Form.Item
            name="poNumber"
            label="PO订单号"
            rules={[{ required: true, message: '请输入PO订单号' }]}
          >
            <Input placeholder="请输入PO订单号" />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入描述信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑装箱清单模态框 */}
      <Modal
        title="编辑装箱清单"
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalVisible(false)
          editForm.resetFields()
          setCurrentPackingList(null)
        }}
        width={600}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入描述信息" />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
          >
            <Select placeholder="请选择状态">
              <Option value="unpacked">未装箱</Option>
              <Option value="packing">装箱中</Option>
              <Option value="completed">已完成</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 装箱明细抽屉 */}
      <Drawer
        title={`装箱明细 - ${currentPackingList?.packingNumber}`}
        placement="right"
        width={1000}
        open={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false)
          setCurrentPackingList(null)
          setPackingDetails([])
          setExpandedRowKeys([])
          setAllExpanded(false)
        }}
      >
        {currentPackingList && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="装箱号">{currentPackingList.packingNumber}</Descriptions.Item>
              <Descriptions.Item label="款号">{currentPackingList.styleNumber}</Descriptions.Item>
              <Descriptions.Item label="款名">{currentPackingList.styleName || '-'}</Descriptions.Item>
              <Descriptions.Item label="PO订单号">{currentPackingList.poNumber}</Descriptions.Item>
              <Descriptions.Item label="状态">{renderStatusTag(currentPackingList.status)}</Descriptions.Item>
              <Descriptions.Item label="箱数">{currentPackingList.boxCount}</Descriptions.Item>
              <Descriptions.Item label="计划数量">{currentPackingList.plannedTotalQty}</Descriptions.Item>
              <Descriptions.Item label="实际数量">{currentPackingList.actualTotalQty}</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(currentPackingList.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                {currentPackingList.description || '无'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0 }}>装箱明细</Title>
              <Space>
                {(currentPackingList.status === 'UNPACKED' || currentPackingList.status === 'PACKING') && 
                 (user?.role === 'ADMIN' || user?.role === 'BUSINESS') && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={handleCreateDetail}
                  >
                    新增明细
                  </Button>
                )}
                <Button
                  type="text"
                  size="small"
                  onClick={handleToggleExpandAll}
                  style={{ 
                    color: '#1890ff',
                    fontWeight: 500,
                    padding: '4px 8px'
                  }}
                >
                  {allExpanded ? '全部收起 -' : '全部展开 +'}
                </Button>
              </Space>
            </div>
            <Table
              columns={detailColumns}
              dataSource={packingDetails}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1000 }}
              rowClassName={(record) => {
                const anomalies = record.anomalies
                if (!anomalies) return ''
                
                // 异常样式
                if (anomalies.hasMissing && (anomalies.hasExtra || anomalies.hasOffList)) return 'row-anomaly-mixed'
                if (anomalies.hasMissing) return 'row-anomaly-missing'
                if (anomalies.hasExtra) return 'row-anomaly-extra'
                if (anomalies.hasOffList) return 'row-anomaly-offlist'
                return ''
              }}
              expandable={{
                expandedRowRender: renderExpandedRow,
                expandedRowKeys: expandedRowKeys,
                onExpandedRowsChange: (keys) => {
                  setExpandedRowKeys([...keys])
                  // 更新全部展开状态
                  const expandableCount = packingDetails.filter(record => 
                    (Array.isArray(record.plannedItems) && record.plannedItems.length > 0) ||
                    (Array.isArray(record.actualItems) && record.actualItems.length > 0)
                  ).length
                  setAllExpanded(keys.length === expandableCount && expandableCount > 0)
                },
                rowExpandable: (record) => 
                  (Array.isArray(record.plannedItems) && record.plannedItems.length > 0) ||
                  (Array.isArray(record.actualItems) && record.actualItems.length > 0)
              }}
            />
          </div>
        )}
      </Drawer>

      {/* 导入装箱规划模态框 */}
      <PackingImportModal
        visible={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        onImport={handleImportSubmit}
      />

      {/* 导出装箱数据模态框 */}
      <PackingExportModal
        visible={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onExport={handleExportSubmit}
        selectedCount={selectedRowKeys.length}
      />

      {/* 装箱明细编辑弹窗（新增 / 编辑共用：箱子信息 + 计划明细） */}
      <Modal
        title={detailModalMode === 'create' ? '新增装箱明细' : '编辑装箱明细'}
        open={detailModalVisible}
        onOk={handleDetailSubmit}
        confirmLoading={detailSubmitting}
        onCancel={closeDetailModal}
        width={880}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={detailForm}
          layout="vertical"
          onValuesChange={(changed) => {
            if (Object.prototype.hasOwnProperty.call(changed, 'colorCode')) {
              schedulePlanPreview(changed.colorCode, planDraft)
            }
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="boxSequence"
                label="箱号"
                rules={[{ required: true, message: '请输入箱号' }]}
              >
                <Input type="number" placeholder="请输入箱号" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="colorCode"
                label="颜色码"
                rules={[{ required: true, message: '请输入颜色码' }]}
              >
                <Input placeholder="请输入颜色码" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="colorChinese" label="中文颜色">
                <Input placeholder="请输入中文颜色" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="colorDescription" label="颜色描述">
                <Input placeholder="请输入颜色描述" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="netWeight" label="净重(kg)">
                <Input type="number" placeholder="请输入净重" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="grossWeight" label="毛重(kg)">
                <Input type="number" placeholder="请输入毛重" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider orientation="left" style={{ marginTop: 0, marginBottom: 12 }}>
          计划明细
        </Divider>

        {planDraft.some((r) => (r.size || '').trim() && !r.found) && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="存在没有对应 SKU 的尺码"
            description="标红的尺码在目标颜色码下找不到 SKU，将无法保存。请删除这些行，或修正颜色码后重试。"
          />
        )}

        <div style={{ marginBottom: 12 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={addPlanRow} block>
            添加尺码
          </Button>
        </div>

        <Table
          dataSource={planDraft.map((item, index) => ({ ...item, key: index }))}
          pagination={false}
          size="small"
          bordered
          locale={{ emptyText: '请点击「添加尺码」' }}
          columns={[
            {
              title: '尺码',
              dataIndex: 'size',
              width: 160,
              render: (text, _record, index) => (
                <Input
                  value={text}
                  onChange={(e) => updatePlanRow(index, 'size', e.target.value)}
                  placeholder="如 M / 38"
                />
              )
            },
            {
              title: '数量',
              dataIndex: 'quantity',
              width: 120,
              render: (text, _record, index) => (
                <Input
                  type="number"
                  min={1}
                  value={text}
                  onChange={(e) => updatePlanRow(index, 'quantity', parseInt(e.target.value) || 0)}
                  placeholder="数量"
                />
              )
            },
            {
              title: '条码（按 款号+颜色码+尺码 自动匹配）',
              dataIndex: 'barcode',
              render: (text, record) =>
                record.found ? (
                  <Typography.Text copyable>{text}</Typography.Text>
                ) : (record.size || '').trim() ? (
                  <Typography.Text type="danger">无对应 SKU</Typography.Text>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                )
            },
            {
              title: '操作',
              width: 90,
              render: (_, _record, index) => (
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removePlanRow(index)}
                >
                  删除
                </Button>
              )
            }
          ]}
        />

        <div style={{ marginTop: 12, color: '#666', fontSize: 14 }}>
          {planResolving
            ? '正在匹配 SKU…'
            : `共 ${planDraft.length} 个尺码，合计 ${planDraft.reduce(
                (sum, item) => sum + (Number(item.quantity) || 0),
                0
              )} 件`}
        </div>
      </Modal>
    </div>
  )
}

// 添加行颜色样式(包括状态和异常)
const packingStyles = document.createElement('style')
packingStyles.textContent = `
  .row-packing {
    background-color: #e6f7ff !important;
  }
  .row-packing:hover > td {
    background-color: #bae7ff !important;
  }
  .row-completed {
    background-color: #f6ffed !important;
  }
  .row-completed:hover > td {
    background-color: #d9f7be !important;
  }
  .row-partial {
    background-color: #fff7e6 !important;
  }
  .row-partial:hover > td {
    background-color: #ffe7ba !important;
  }
  .row-anomaly-missing {
    background-color: #fff1f0 !important;
  }
  .row-anomaly-missing:hover > td {
    background-color: #ffccc7 !important;
  }
  .row-anomaly-extra {
    background-color: #fff7e6 !important;
  }
  .row-anomaly-extra:hover > td {
    background-color: #ffd591 !important;
  }
  .row-anomaly-offlist {
    background-color: #e6f7ff !important;
  }
  .row-anomaly-offlist:hover > td {
    background-color: #bae7ff !important;
  }
  .row-anomaly-mixed {
    background-color: #f9f0ff !important;
  }
  .row-anomaly-mixed:hover > td {
    background-color: #d3adf7 !important;
  }
`
if (!document.getElementById('packing-row-styles')) {
  packingStyles.id = 'packing-row-styles'
  document.head.appendChild(packingStyles)
}

export default PackingManagement